import _ from "lodash";
import React from "react";
import {ApiUtility, LoginResponse} from "../common/ApiUtility";
import {Constants} from "../common/Constants";
import {EmailUtility} from "../common/EmailUtility";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {PillButton} from "../common/PillButton";
import {CookieUtility} from "../common/CookieUtility";
import {AuthUtility} from "../common/AuthUtility";
import {OtpLoginView} from "./OtpLoginView";

export let LoginPage = function () {
    let dataStore = useRenderableDataStore({
        view: 'email',
        email: '',
        username: '',
        password: '',
        submittedEmail: '',
        code: '',
        emailInvalid: false,
        loading: false
    });

    function handleSuccessfulAuth(response: LoginResponse) {
        let permissionNames = _.map(response.permissions || [], 'name');

        if (_.includes(permissionNames, Constants.permissionNames.canAccessWebAdmin)) {
            let cookieParams = {
                path: '/',
                expires: new Date(2999, 1, 1, 0, 0, 1)
            };

            AuthUtility.clearAuthCookies();
            CookieUtility.save("permissionNames", permissionNames, cookieParams);

            window.location.assign('/dashboard');
            return true;
        } else {
            MessageDisplayerUtility.error('User does not have web admin access.');
            return false;
        }
    }

    function login() {
        ApiUtility.login(dataStore.data().username, dataStore.data().password)
            .then(function (response: LoginResponse) {
                if (response.user) {
                    handleSuccessfulAuth(response);
                } else {
                    MessageDisplayerUtility.error('Incorrect username or password.');
                }
            })
            .catch(function (errorMessage) {
                MessageDisplayerUtility.error(errorMessage);
            });
    }

    function requestOtp() {
        let email = dataStore.data().email;

        if (!EmailUtility.isValid(email)) {
            dataStore.updateAndRender({emailInvalid: true});
            return;
        }

        dataStore.updateAndRender({loading: true});
        ApiUtility.requestLoginOtp(email)
            .then(function () {
                dataStore.updateAndRender({
                    view: 'otp',
                    submittedEmail: email,
                    code: '',
                    loading: false
                });
            })
            .catch(function (errorMessage) {
                MessageDisplayerUtility.error(errorMessage);
                dataStore.updateAndRender({loading: false});
            });
    }

    function verifyOtp() {
        dataStore.updateAndRender({loading: true});
        ApiUtility.verifyLoginOtp(dataStore.data().submittedEmail, dataStore.data().code)
            .then(function (response: LoginResponse) {
                if (response.user) {
                    let redirected = handleSuccessfulAuth(response);
                    if (!redirected) {
                        dataStore.updateAndRender({loading: false});
                    }
                } else {
                    MessageDisplayerUtility.error('The code you entered is invalid or has expired.');
                    dataStore.updateAndRender({code: '', loading: false});
                }
            })
            .catch(function (errorMessage) {
                MessageDisplayerUtility.error(errorMessage);
                dataStore.updateAndRender({loading: false});
            });
    }

    function handleTextFieldChange(fieldName: string, event: React.ChangeEvent<HTMLInputElement>) {
        dataStore.updateAndRender({[fieldName]: event.target.value});
    }

    function showUsernamePasswordView() {
        dataStore.updateAndRender({view: 'usernamePassword'});
    }

    function backToEmailView() {
        dataStore.updateAndRender({view: 'email', code: ''});
    }

    function renderEmailView() {
        return (
            <form
                className="common-form pre-login-form login-form"
                onSubmit={
                    (event: React.SyntheticEvent) => {
                        if (event) {
                            event.preventDefault();
                        }
                        requestOtp()
                    }
                }
            >
                <div className="common-form-body">
                    <div className="common-form-body-row">
                        <div className="common-form-field pre-login-field">
                            <div className="common-form-field-label">EMAIL</div>
                            <div className="common-form-field-input-container">
                                <input
                                    id="emailInput"
                                    autoFocus={true}
                                    className={"common-form-input" + (dataStore.data().emailInvalid ? " invalid" : "")}
                                    type="text"
                                    placeholder="EMAIL"
                                    onChange={(event) => handleTextFieldChange('email', event)}
                                    value={dataStore.data().email}
                                />
                            </div>
                        </div>
                    </div>
                    <button className="enter-submit-support-button"></button>
                    <PillButton
                        buttonClasses="solid-green-button"
                        onClick={requestOtp}
                        buttonText="LOGIN"
                    />
                    <div className="login-switch-link-container">
                        <a
                            className="login-switch-to-username-link"
                            onClick={(event) => {
                                event.preventDefault();
                                showUsernamePasswordView();
                            }}
                        >Log in with username/password instead &raquo;</a>
                    </div>
                    <div className="forgot-password-container">
                        <a
                            className="forgot-password-link"
                            href="/forgot-password"
                        >Forgot Password?</a>
                    </div>
                </div>
            </form>
        );
    }

    function renderUsernamePasswordView() {
        return (
            <form
                className="common-form pre-login-form login-form"
                onSubmit={
                    (event: React.SyntheticEvent) => {
                        if (event) {
                            event.preventDefault();
                        }
                        login()
                    }
                }
            >
                <div className="common-form-body">
                    <div className="common-form-body-row">
                        <div className="common-form-field pre-login-field">
                            <div className="common-form-field-input-container">
                                <input
                                    id="usernameInput"
                                    autoFocus={true}
                                    className="common-form-input"
                                    type="text"
                                    placeholder="USERNAME"
                                    onChange={(event) => handleTextFieldChange('username', event)}
                                    value={dataStore.data().username}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="common-form-body-row">
                        <div className="common-form-field pre-login-field">
                            <div className="common-form-field-input-container">
                                <input
                                    id="passwordInput"
                                    className="common-form-input"
                                    type="password"
                                    placeholder="PASSWORD"
                                    onChange={(event) => handleTextFieldChange('password', event)}
                                    value={dataStore.data().password}
                                />
                            </div>
                        </div>
                    </div>
                    <button className="enter-submit-support-button"></button>
                    <PillButton
                        buttonClasses="solid-green-button"
                        onClick={login}
                        buttonText="LOGIN"
                    />
                    <div className="login-switch-link-container">
                        <a
                            className="login-switch-to-email-link"
                            onClick={(event) => {
                                event.preventDefault();
                                backToEmailView();
                            }}
                        >Log in with email instead &raquo;</a>
                    </div>
                    <div className="forgot-password-container">
                        <a
                            className="forgot-password-link"
                            href="/forgot-password"
                        >Forgot Password?</a>
                    </div>
                </div>
            </form>
        );
    }

    return (
        <div className="pre-login-page login-page">
            <ConditionalRenderer
                if={dataStore.data().loading}
                renderer={() => <OverlayLoadingIndicator/>}
            />
            <div className="pre-login-page-content login-page-content">
                <div className="pre-login-page-header">
                    <img
                        src="/static/images/berger-logo-full-light-gray.svg"
                        className="pre-login-page-logo"
                    />
                </div>
                {(() => {
                    if (dataStore.data().view === 'otp') {
                        return (
                            <OtpLoginView
                                email={dataStore.data().submittedEmail}
                                code={dataStore.data().code}
                                onCodeChange={(value: string) => dataStore.updateAndRender({code: value})}
                                onSubmit={verifyOtp}
                                onCancel={backToEmailView}
                            />
                        );
                    } else if (dataStore.data().view === 'usernamePassword') {
                        return renderUsernamePasswordView();
                    } else {
                        return renderEmailView();
                    }
                })()}
                <div className="pre-login-page-footer">
                    <div className="pre-login-page-footer-label">
                        <div className="pre-login-page-footer-label-line" />
                        <div className="pre-login-page-footer-label-text">
                            Truck Dispatch
                        </div>
                    </div>
                    <div className="version-info">
                        {`Version ${document.querySelector('.app-version')?.innerHTML || 'Unknown'}`}
                    </div>
                </div>
            </div>
        </div>
    );
};
